/**
 * PWA — Service Worker registration + install button hook.
 *
 * Comment ça marche :
 *   1. Au chargement de l'app, on enregistre /service-worker.js (s'il est servi).
 *   2. On écoute l'événement `beforeinstallprompt` que Chrome/Edge déclenchent
 *      QUAND l'app est installable (manifest valide + SW enregistré + critères
 *      d'engagement remplis : visite > 30s ou interaction utilisateur).
 *   3. On stocke le prompt natif différé pour le déclencher au clic du bouton
 *      d'installation côté UI.
 *
 * Hooks exposés :
 *   - registerServiceWorker() : à appeler une fois dans App.js
 *   - usePwaInstall() : hook React qui expose { canInstall, install, isInstalled }
 *
 * Notes :
 *   - Le `display: standalone` du manifest + un user-gesture sont obligatoires
 *     pour qu'iOS Safari montre "Ajouter à l'écran d'accueil" — pas automatique.
 *   - Sur desktop Windows/Mac, le bouton "Installer Auto-Gère" apparaît dans la
 *     barre d'adresse Chrome/Edge dès que les critères sont remplis. Le bouton
 *     custom dans l'UI sert de fallback / de découvrabilité.
 */
import { useEffect, useState, useCallback } from 'react';

let deferredPromptRef = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chrome veut afficher son mini-bandeau natif — on l'intercepte pour
    // contrôler l'apparition via notre bouton.
    e.preventDefault();
    deferredPromptRef = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPromptRef = null;
    notify();
  });
}

export const registerServiceWorker = () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  // Délai léger pour ne pas concurrencer le boot React initial.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((reg) => { reg.update().catch(() => undefined); })
      .catch(() => undefined);
  });
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export const usePwaInstall = () => {
  const [canInstall, setCanInstall] = useState(!!deferredPromptRef);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const update = () => {
      setCanInstall(!!deferredPromptRef);
      setInstalled(isStandalone());
    };
    listeners.add(update);
    update();
    return () => listeners.delete(update);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPromptRef) return false;
    try {
      deferredPromptRef.prompt();
      const choice = await deferredPromptRef.userChoice;
      deferredPromptRef = null;
      notify();
      return choice.outcome === 'accepted';
    } catch {
      return false;
    }
  }, []);

  return { canInstall, install, isInstalled: installed };
};
